import { useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import ControlSelect from "~app/ControlSelect"
import PortfolioForm from "~components/portfolio/PortfolioForm"
import { usePortfolioData } from "~hooks/usePortfolioData"
import usePortfolio from "~hooks/usePortfolio"
import { createDropdownCollection } from "~utils/dropdown"
import { PORTFOLIO_TYPE } from "~shapes/portfolio"
import { ButtonPrimarySoftLarge } from "~styled/button"
import FlexCol from "~components/FlexCol"
import FlexRow from "~components/FlexRow"
import { useMutation } from "react-query"
import PortfolioService from "~services/PortfolioService"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { formatDateTimeISO } from "~utils/utils"

interface Props {
  userId: string
  userName?: string
  brokerName?: string
  month?: number
  year?: number
  showPooFields?: boolean
  showSubmitButton?: boolean
  enableBrokerSelect?: boolean
  form?: ReturnType<typeof useForm>
  isLoading?: boolean
  userEmail?: string
  aiEnhancedPortfolio?: any[]
  aiFilledFields?: Set<string>
  aiSuggestsReject?: boolean
  aiSuggestsReview?: boolean
  securities?: any[]
  columns?: number
  hideEmpty?: boolean
  stackLabel?: boolean
}

const Portfolio = ({
  userId,
  userName,
  brokerName,
  month,
  year,
  showPooFields = false,
  showSubmitButton = true,
  enableBrokerSelect = false,
  form,
  isLoading,
  userEmail,
  aiEnhancedPortfolio,
  aiFilledFields,
  aiSuggestsReject = false,
  aiSuggestsReview = false,
  securities,
  columns,
  hideEmpty,
  stackLabel = false,
}: Props) => {
  const methods =
    form ||
    useForm({
      defaultValues: { selected_broker: null },
    })

  const { control, watch, getValues } = methods
  const watchedBroker = watch("selected_broker")
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null)
  const [creationType, setCreationType] = useState(PORTFOLIO_TYPE.MANUAL)
  const [hasProofOfOwnership, setHasProofOfOwnership] = useState(false)
  const queryClient = useQueryClient()

  const isEditable =
    creationType === PORTFOLIO_TYPE.MANUAL || hasProofOfOwnership

  const { mutateAsync: invalidatePortfolio, isLoading: isInvalidating } =
    useMutation(payload => PortfolioService.invalidatePortfolio(payload), {
      onSuccess: async () => {
        toast.success("Portfolio invalidated successfully")
        await queryClient.invalidateQueries(["user-portfolio", userId])
        const newData: any[] = await refetch().then(r => r.data)

        const firstValid = newData?.find(p => p.state === "VALID")
        if (firstValid?.broker) {
          const newBroker = {
            label: firstValid.broker,
            value: firstValid.broker,
          }

          methods.reset({
            ...methods.getValues(),
            selected_broker: newBroker,
          })

          setSelectedBroker(firstValid.broker)
        } else {
          methods.reset({
            ...methods.getValues(),
            selected_broker: null,
          })
          setSelectedBroker(null)
        }
      },
      onError: () => {
        toast.error("Failed to invalidate portfolio")
      },
    })

  const extractValidPortfolio = (rawData: any[], broker?: string) => {
    const validPortfolios = rawData.filter(p => p.state === "VALID")

    if (validPortfolios.length === 0) return null

    const selected = validPortfolios.find(p => p.broker === broker)
    return selected || validPortfolios[0]
  }

  const {
    portfolio,
    selectedPortfolio,
    brokerName: existingBroker,
    isLoading: isLoadingPortfolio,
    existPortfolio,
    refetch,
    rawPortfolios: data,
  } = usePortfolioData(
    usePortfolio,
    userId,
    extractValidPortfolio,
    selectedBroker,
    securities,
  )

  useEffect(() => {
    setSelectedBroker(watchedBroker?.value)
  }, [watchedBroker])

  useEffect(() => {
    if (enableBrokerSelect && data?.length > 0) {
      const validBrokers = data.filter(p => p.state === "VALID")
      const defaultBroker = validBrokers[0]?.broker

      if (!watchedBroker?.value && defaultBroker) {
        const defaultOption = { label: defaultBroker, value: defaultBroker }

        if (getValues("selected_broker")?.value !== defaultBroker) {
          methods.reset({
            ...methods.getValues(),
            selected_broker: defaultOption,
          })
          setSelectedBroker(defaultBroker)
        }
      }
    }
  }, [data, enableBrokerSelect])

  useEffect(() => {
    if (selectedPortfolio?.creationType) {
      setCreationType(selectedPortfolio.creationType)
    } else {
      setCreationType(PORTFOLIO_TYPE.MANUAL)
    }
    setHasProofOfOwnership(!!selectedPortfolio?.hasProofOfOwnership)
  }, [selectedPortfolio])

  const createBrokerDropdown = () => {
    const valid = data?.filter(p => p.state === "VALID") || []
    const options = valid.map(p => ({ label: p.broker, name: p.broker }))
    return createDropdownCollection(options, "label", "name")
  }

  const handleInvalidatePortfolio = async () => {
    if (!selectedPortfolio) return
    if (
      confirm(
        "This action can't be reverted, are you sure you want to invalidate this portfolio?",
      )
    ) {
      const payload = {
        ...selectedPortfolio,
        userId,
        state: "INVALID",
      }
      await invalidatePortfolio(payload)
    }
  }

  const refetchPortfolio = async () => {
    queryClient.removeQueries(["user-aggregated-portfolio", userId])
    await refetch()
  }

  return (
    <FormProvider {...methods}>
      {enableBrokerSelect && existPortfolio && (
        <FlexRow style={{ alignItems: "center", gap: 10 }}>
          <FlexCol>
            <ControlSelect
              name="selected_broker"
              label="Broker"
              control={control}
              options={createBrokerDropdown()}
              isMulti={false}
              isLoading={isLoadingPortfolio}
            />
          </FlexCol>

          <FlexCol
            style={{
              marginBottom: 10,
            }}
          >
            {isEditable ? (
              <div
                style={{
                  justifyContent: "space-evenly",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <p style={{ margin: 0 }}>
                  Updated at: {formatDateTimeISO(selectedPortfolio.date)}
                </p>

                <ButtonPrimarySoftLarge
                  onClick={handleInvalidatePortfolio}
                  disabled={isInvalidating}
                >
                  {isInvalidating ? "Invalidating..." : "Invalidate Portfolio"}
                </ButtonPrimarySoftLarge>
              </div>
            ) : (
              <div style={{ color: "#888", fontSize: 13 }}>
                To disconnect this <strong>automatic</strong> portfolio go to{" "}
                <strong>Broker connections</strong> tab
              </div>
            )}
          </FlexCol>
        </FlexRow>
      )}

      <PortfolioForm
        userId={userId}
        userName={userName}
        userEmail={userEmail}
        brokerName={brokerName ?? selectedBroker ?? existingBroker ?? ""}
        month={month}
        year={year}
        portfolio={portfolio}
        isLoading={isLoadingPortfolio || isLoading}
        showSubmitBtn={showSubmitButton && isEditable}
        showPooFields={showPooFields}
        existPortfolio={existPortfolio}
        onRefetchPortfolios={refetchPortfolio}
        disableFields={!isEditable}
        aiEnhancedPortfolio={aiEnhancedPortfolio}
        aiFilledFields={aiFilledFields}
        aiSuggestsReject={aiSuggestsReject}
        aiSuggestsReview={aiSuggestsReview}
        columns={columns}
        hideEmpty={hideEmpty}
        securities={securities}
        stackLabel={stackLabel}
      />
    </FormProvider>
  )
}

export default Portfolio
